import React, { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { messages } from './messages';
import { HAS_DISMISSED_INSURANCE_COVERAGE_BANNER, useUserSpecificLocalStorage } from '../../../hooks/use-local-storage';
import { US } from '../../../constants/country-codes';
import { getAutomationsSettingsPath } from '../../../routes/constants';
import Global from '../../../store/global-selectors';
import { useWalletEligibility } from '../../../wallet/hooks/use-wallet-eligibility';
import style from '../header-notification-banner.module.less';

/**
 * Insurance coverage notification banner component
 * Displays a warning about carrier insurance coverage changes with link to automation rules
 * @returns {JSX.Element} The banner component
 */
export const InsuranceCoverageBanner = memo(() => {
  const [, setBannerDismissalStatus] = useUserSpecificLocalStorage(HAS_DISMISSED_INSURANCE_COVERAGE_BANNER);
  const navigate = useNavigate();
  const homeCountry = useSelector(Global.selectSellerHomeCountry);
  const walletEligibility = useWalletEligibility();

  const onClose = useCallback(() => {
    setBannerDismissalStatus(true);
  }, [setBannerDismissalStatus]);

  const handleOnClick = useCallback(() => {
    navigate(getAutomationsSettingsPath());
  }, [navigate]);

  // Only show banner for US users with wallets configured
  if (homeCountry !== US || !walletEligibility) {
    return null;
  }

  return (
    <BannerMessageV2
      className={style['banner-padding']}
      content={
        <Text
          formattedMessageValues={{
            a: (chunks) => (
              <Button onClick={handleOnClick} theme={THEME_CONSTANTS.link}>
                {chunks}
              </Button>
            ),
          }}
          textType={TextType.Paragraph}
          value={messages.content}
        />
      }
      layout={BannerLayout.Default}
      onClose={onClose}
      status={BannerStatus.Warning}
    />
  );
});
